// client/src/pages/Terms.js
import { useSettings } from '../hooks/useSettings.js';

export default function Terms() {
  const { settings } = useSettings();
  const siteName = settings.site_name || 'The Cherry Fairy Shop';

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl text-gold-dark dark:text-gold mb-2">Terms of Service</h1>
        <p className="text-royal-500 dark:text-royal-200 text-base mb-8">Last updated: May 4, 2026</p>

        <div className="space-y-8 text-royal-700 dark:text-royal-100 text-base leading-relaxed">

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Acceptance of Terms</h2>
            <p>
              By creating an account or using {siteName} ("the Service"), you agree to be bound by these 
              Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. 
              {siteName} is operated by Todd Davis as a sole proprietorship.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Description of Service</h2>
            <p>
              {siteName} is a digital eCard platform that allows users to create, personalize, and send 
              animated greeting cards with music to recipients via email. The Service includes free and 
              paid subscription tiers with varying features such as contact management, scheduled sending, 
              birthday auto-send, analytics, and business-oriented tools.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Account Registration</h2>
            <p>
              To use the Service, you must create an account with a valid email address. You are responsible 
              for maintaining the confidentiality of your account credentials and for all activity that occurs 
              under your account. You must provide accurate information during registration and keep it up to date. 
              You must be at least 13 years of age to create an account. eCards are delivered via email to the 
              email address on file for each contact. When sending eCards intended for minors (such as a birthday 
              card for a child), the eCard should be addressed to the parent or guardian's email address. The 
              household members feature allows you to track birthdays for family members within a household 
              without requiring a separate email address for each individual.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <p className="mt-2">
              Send emails through the Service to recipients who have previously opted out or unsubscribed; 
              use the Service to send deceptive, misleading, or fraudulent content; harass, threaten, or 
              abuse others; send content that is unlawful, defamatory, obscene, or infringes on the rights 
              of others; impersonate another person or entity; attempt to gain unauthorized access to the 
              Service or its systems; upload malicious code, viruses, or harmful content; use the Service 
              to promote, advertise, or link to adult or sexually explicit content, services, or websites, 
              including but not limited to escort services, pornography, or adult subscription platforms; 
              or use the Service in any manner that could damage, disable, or impair the Service.
            </p>
            <p className="mt-3">
              If you intend to send eCards to clients, prospects, or other business contacts who have not 
              explicitly requested them, you must subscribe to our Business Wizard plan. The Business Wizard 
              plan includes an unsubscribe mechanism that allows recipients to opt out of future communications, 
              ensuring compliance with applicable email regulations including CAN-SPAM. Sending commercial or 
              promotional eCards to business contacts on a personal plan (such as Fairydust) is not permitted, 
              as personal plans do not include the required unsubscribe functionality.
            </p>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate these terms without prior notice.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Contact Data and Responsibility</h2>
            <p>
              By adding contacts to your account — whether manually, via CSV import, or through Google or 
              Microsoft contact import — you represent and warrant that you have the right to provide this 
              information and that the individuals whose data you provide have not asked you to stop contacting 
              them. You are solely responsible for the accuracy and lawfulness of the contact data you enter 
              into the Service.
            </p>
            <p className="mt-3">
              When using the Google or Microsoft contact import feature, you authorize us to access your 
              contacts on a one-time, read-only basis for the sole purpose of importing them into your 
              {siteName} contact list. We do not retain access to your Google or Microsoft account beyond 
              the import session.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Subscriptions and Payments</h2>
            <p>
              {siteName} offers free and paid subscription plans. Paid plans are billed on a monthly or 
              annual basis through Stripe. By subscribing to a paid plan, you authorize us to charge your 
              payment method at the beginning of each billing cycle.
            </p>
            <p className="mt-3">
              Prices are displayed on our Pricing page and are subject to change. Any price changes will 
              take effect at the start of your next billing cycle, not during your current one.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Cancellation and Refunds</h2>
            <p>
              You may cancel your subscription at any time from your Account page.
            </p>
            <p className="mt-3">
              For annual plans, if you cancel early you will receive a prorated refund for the unused months 
              remaining, and your account access ends immediately upon cancellation.
            </p>
            <p className="mt-3">
              For monthly plans, there are no refunds. You retain full access until the end of your current 
              billing period and will not be charged again after that. You may re-subscribe at any time.
            </p>
            <p className="mt-3">
              <strong className="text-royal-900 dark:text-white">Cancellations triggered by gift redemption</strong> follow 
              different rules — see the "Gift Subscriptions" section below.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Free Trials</h2>
            <p>
              We may offer free trials for certain subscription tiers. Free trials are limited to one per 
              account. When a trial expires, your account reverts to the free tier unless you subscribe to 
              a paid plan. We will not charge you automatically at the end of a trial — a subscription 
              requires a separate action on your part.
            </p>
            <p className="mt-3">
              If you redeem a gift subscription during an active free trial, your trial will end immediately 
              and your gift subscription will begin. The remaining time on your trial is forfeited.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Gift Subscriptions</h2>
            <p>
              Gift subscriptions are non-refundable once the gift code has been delivered. Gift codes have 
              an expiration date and must be redeemed before that date. Unused or expired gift codes cannot 
              be refunded or extended.
            </p>

            <p className="mt-3">
              <strong className="text-royal-900 dark:text-white">Redeeming a gift while on an existing plan.</strong>{' '}
              The behavior depends on whether the gift is for the same plan you currently have or a different one:
            </p>

            <ul className="mt-3 ml-5 list-disc space-y-2">
              <li>
                <strong className="text-royal-900 dark:text-white">Same plan as your current paid subscription:</strong>{' '}
                The gift "stacks" on top of your subscription. Your auto-renewing subscription will be 
                set to cancel at the end of its current billing period (no further charges), and your gift 
                subscription will begin the day after your paid period ends. The total time on your plan 
                is the remaining paid period plus the gift duration.
              </li>
              <li>
                <strong className="text-royal-900 dark:text-white">Same plan as an existing active gift:</strong>{' '}
                The new gift extends your existing gift's expiration by the new gift's duration (typically one year). 
                For example, if you have three months remaining on a Fairydust gift and redeem another one-year 
                Fairydust gift, your subscription will end fifteen months from today.
              </li>
              <li>
                <strong className="text-royal-900 dark:text-white">Different plan from your current annual subscription:</strong>{' '}
                You may choose to switch. If you confirm, your current annual subscription is cancelled immediately 
                with a prorated refund for the unused portion (consistent with our standard annual cancellation policy), 
                and the gift begins today. You may also choose to save the gift code for later instead.
              </li>
              <li>
                <strong className="text-royal-900 dark:text-white">Different plan from your current monthly subscription:</strong>{' '}
                You may choose to switch. If you confirm, your monthly subscription is cancelled immediately and 
                the gift begins today. <strong className="text-royal-900 dark:text-white">No refund is issued for the 
                current month</strong> — this is a deliberate exception to our standard monthly cancellation policy 
                because gift redemption is an immediate substitution rather than a normal cancellation. You may also 
                choose to save the gift code for later instead.
              </li>
              <li>
                <strong className="text-royal-900 dark:text-white">Different plan from an existing active gift:</strong>{' '}
                If you choose to redeem the new gift, your existing gift is replaced and any remaining time on the 
                old gift is forfeited. You may also choose to save the new gift code for later instead.
              </li>
            </ul>

            <p className="mt-3">
              In all cases where a confirmation is required, the redemption page displays the consequences of your 
              choice — including the new expiration date, refund amount (if applicable), and any time forfeited — 
              before you confirm. Once you confirm a redemption, the change is final and cannot be reversed.
            </p>

            <p className="mt-3">
              When a paid subscription is cancelled as part of a gift redemption (whether at period end for 
              same-plan stacking or immediately for plan switching), auto-renewal stops permanently. To resume 
              your previous plan after the gift expires, you would need to subscribe again from the Pricing page.
            </p>

            <p className="mt-3">
              Each email address may have only one active or redeemed gift at a time. If you receive a gift code 
              and cannot redeem it (for example, because you already have an active gift in your account), the 
              code remains valid until its expiration date and may be shared with someone else.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Intellectual Property</h2>
            <p>
              All eCard templates, animations, artwork, music, stories, and other creative content provided 
              through the Service are owned by or licensed to {siteName}. You are granted a limited, 
              non-exclusive, non-transferable license to use this content solely for the purpose of creating 
              and sending eCards through the Service.
            </p>
            <p className="mt-3">
              You may not copy, reproduce, distribute, modify, or create derivative works from our content 
              for any purpose outside of the Service. Music included in eCards is licensed for use within 
              the Service only and may not be downloaded, extracted, or used separately.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">User Content</h2>
            <p>
              You retain ownership of any personal messages, photos, or other content you create within eCards. 
              By using the Service, you grant us a limited license to process, store, and transmit your content 
              solely to deliver your eCards to the intended recipients.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">eCard Delivery and Expiration</h2>
            <p>
              We make reasonable efforts to deliver eCards promptly, but do not guarantee delivery times. 
              Email delivery depends on recipient email providers and factors outside our control. 
              eCards have a lifespan determined by your subscription tier (displayed on the Pricing page). 
              After expiration, the eCard link will no longer be accessible. We are not responsible for 
              eCards that are not viewed before expiration.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Email Delivery and Recipient Rights</h2>
            <p>
              All eCard notification emails include an unsubscribe option. When a recipient unsubscribes, 
              they are added to a suppression list and you will be prevented from sending future eCards 
              to that address. You must respect unsubscribe requests and may not attempt to circumvent 
              this restriction by creating new accounts or altering email addresses.
            </p>
            <p className="mt-3">
              You are responsible for maintaining accurate and valid contact information. Sending eCards 
              to invalid, outdated, or mistyped email addresses results in bounces, which can harm our 
              ability to deliver emails for all users. If your account generates excessive bounces 
              (emails that fail to deliver) or spam complaints (recipients marking your eCards as spam), 
              we may temporarily or permanently suspend your sending privileges.
            </p>
            <p className="mt-3">
              We monitor bounce and complaint rates to protect our email reputation and ensure reliable 
              delivery for all users. Accounts that consistently harm deliverability may be terminated 
              without refund. If your sending privileges are suspended, you will be notified and given 
              an opportunity to address the issue before permanent action is taken.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Third-Party Services</h2>
            <p>
              The Service integrates with third-party services including Stripe (payment processing), 
              Amazon SES (email delivery), Google (contact import), and Microsoft (contact import). 
              Your use of these integrations is subject to the respective third party's terms and privacy policies. 
              We are not responsible for the practices of third-party services.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Account Termination</h2>
            <p>
              You may delete your account at any time from your Account page. Account deletion is processed 
              within 30 days, after which all associated data is permanently removed.
            </p>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate these Terms, engage in abusive 
              behavior, or use the Service for spam or other harmful purposes. In cases of termination for 
              violations, refunds are not guaranteed.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Disclaimer of Warranties</h2>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, either 
              express or implied. We do not warrant that the Service will be uninterrupted, error-free, 
              or secure. We do not guarantee that eCards will be delivered or viewed by recipients.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, {siteName} and its operator shall not be liable for 
              any indirect, incidental, special, consequential, or punitive damages arising from your use 
              of the Service, including but not limited to loss of data, loss of revenue, or inability to 
              send or receive eCards. Our total liability for any claim shall not exceed the amount you 
              paid to us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we make significant changes, we will notify 
              registered users via email. Continued use of the Service after changes take effect constitutes 
              your acceptance of the revised Terms. The "Last updated" date at the top indicates when these 
              Terms were last revised.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of Utah, 
              United States, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-royal-900 dark:text-white mb-3">Contact Us</h2>
            <p>
              If you have any questions about these Terms, contact us at:
            </p>
            <p className="mt-2">
              <span className="text-royal-900 dark:text-white font-medium">{siteName}</span><br />
              Todd Davis<br />
              <a href="mailto:thecherryfairies@gmail.com" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
                thecherryfairies@gmail.com
              </a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}